export interface AppBuildInfo {
  app: string
  version: string
  gitSha: string
  buildTime: string
  buildId: string
}

export interface AppUpdateFoundDetail {
  current: AppBuildInfo
  next: AppBuildInfo
}

export const CURRENT_BUILD: AppBuildInfo = __PG19_BUILD_INFO__

const VERSION_URL = '/version.json'
const DEFAULT_POLL_INTERVAL_MS = 5 * 60 * 1000

function isBuildInfo(value: unknown): value is AppBuildInfo {
  if (!value || typeof value !== 'object') return false
  const candidate = value as Partial<Record<keyof AppBuildInfo, unknown>>
  return (
    typeof candidate.app === 'string' &&
    typeof candidate.version === 'string' &&
    typeof candidate.gitSha === 'string' &&
    typeof candidate.buildTime === 'string' &&
    typeof candidate.buildId === 'string'
  )
}

export function isDifferentBuild(current: AppBuildInfo, next: AppBuildInfo) {
  return current.buildId.length > 0 && next.buildId.length > 0 && current.buildId !== next.buildId
}

export async function fetchServerBuildInfo(): Promise<AppBuildInfo | null> {
  const url = new URL(VERSION_URL, window.location.origin)
  url.searchParams.set('t', String(Date.now()))

  const response = await fetch(url, {
    cache: 'no-store',
    headers: { 'Cache-Control': 'no-cache' },
  })
  if (!response.ok) return null

  const data: unknown = await response.json()
  return isBuildInfo(data) ? data : null
}

export async function checkForAppUpdate(registration: ServiceWorkerRegistration) {
  const next = await fetchServerBuildInfo()
  if (!next || !isDifferentBuild(CURRENT_BUILD, next)) return false

  window.dispatchEvent(
    new CustomEvent<AppUpdateFoundDetail>('pg19:app-update-found', {
      detail: { current: CURRENT_BUILD, next },
    }),
  )

  await registration.update()
  return true
}

export function startAppVersionPolling(
  registration: ServiceWorkerRegistration,
  intervalMs = DEFAULT_POLL_INTERVAL_MS,
) {
  let inFlight = false

  const run = () => {
    if (inFlight || document.visibilityState === 'hidden') return
    inFlight = true
    checkForAppUpdate(registration)
      .catch((error) => {
        console.warn('[app version] update check failed:', error)
      })
      .finally(() => {
        inFlight = false
      })
  }

  const intervalId = window.setInterval(run, intervalMs)
  window.setTimeout(run, 10_000)

  const handleVisibilityChange = () => {
    if (document.visibilityState === 'visible') run()
  }
  document.addEventListener('visibilitychange', handleVisibilityChange)

  return () => {
    window.clearInterval(intervalId)
    document.removeEventListener('visibilitychange', handleVisibilityChange)
  }
}
